import { clsx } from "clsx";
import {
  cloneElement,
  type CSSProperties,
  type FC,
  forwardRef,
  type JSX,
  type RefObject,
  useEffect,
  useRef,
} from "react";
import type { AriaDialogProps, OverlayProps } from "react-aria";
import {
  FocusScope,
  mergeProps,
  OverlayContainer,
  useDialog,
  useModal,
  useOverlay,
  useOverlayPosition,
  useOverlayTrigger,
  useVisuallyHidden,
} from "react-aria";
import {
  type MenuTriggerState,
  type OverlayTriggerState,
  useMenuTriggerState,
  useOverlayTriggerState,
} from "react-stately";

import style from "../styles/Overlays.module.scss";

export interface ModalChildProps {
  close: () => void;
}

interface IPopOverModalProps extends Omit<AriaDialogProps, "children">, OverlayProps {
  title?: string;
  actionWidget: FC<Omit<MenuTriggerState, "focusStrategy">>;
  children: FC<ModalChildProps>;
  onCancel?: () => void;
  style?: CSSProperties;
}

interface IPositionOverlayProps {
  positionOffset?: number;
  targetRef: RefObject<HTMLElement | null>;
}

const ModalOverlay = forwardRef<HTMLDivElement, Omit<IPopOverModalProps, "actionWidget"> & { lightOverlay?: boolean }>(
  function ModalOverlay(props, ref) {
    const { id, style: positionStyle, children, ...tempProps } = props;
    const { shouldCloseOnInteractOutside: shouldClose, ...otherProps } = tempProps;

    // Used to detect touch event element to control isOpen state by default.
    const MODAL_EVENT_CLASS_ID = "modal-overlay";
    const shouldCloseOnInteractOutside: OverlayProps["shouldCloseOnInteractOutside"] = (element): boolean => {
      if (element.classList.contains(MODAL_EVENT_CLASS_ID)) return true;
      return shouldClose != undefined ? shouldClose(element) : false;
    };
    const overlayInitProps = { ...otherProps, shouldCloseOnInteractOutside };

    const { overlayProps } = useOverlay(overlayInitProps, ref as RefObject<HTMLElement | null>);
    const { modalProps } = useModal();
    const { dialogProps, titleProps } = useDialog(overlayInitProps, ref as RefObject<HTMLElement | null>);
    const { visuallyHiddenProps } = useVisuallyHidden();

    // usePreventScroll(); // causes SEL8-643 (Firefox only, after some scrolling)

    // TODO: At best, the titleProps should be passed and retrieved from the PageHeader.title, if any
    return (
      // Don't remove MODAL_EVENT_CLASS_ID class. Also added "modal-overlay" as string so that it can be traced back correctly by IntelliJ
      <div
        className={clsx("modal-overlay", MODAL_EVENT_CLASS_ID, style.overlay, {
          [style.overlay_transparent]: props.lightOverlay,
        })}
      >
        <div
          {...mergeProps(overlayProps, dialogProps, modalProps)}
          ref={ref}
          style={{ maxHeight: "100%", ...positionStyle, display: "flex" }}
          id={id}
        >
          <h3 {...mergeProps(titleProps, visuallyHiddenProps)}>{props.title}</h3>
          {children}
        </div>
      </div>
    );
  },
);

/**
 * Use useMenu instead
 * @deprecated
 */
const MenuOverlay = forwardRef<HTMLDivElement, Omit<IPopOverModalProps, "actionWidget" | "title">>(
  function MenuOverlay(props, ref) {
    const { id, style: positionStyle, children, ...otherProps } = props;
    const { overlayProps } = useOverlay(otherProps, ref as RefObject<HTMLElement | null>);

    return (
      <FocusScope contain restoreFocus autoFocus>
        <div {...overlayProps} ref={ref} style={{ ...positionStyle, display: "flex" }} id={id}>
          {children}
        </div>
      </FocusScope>
    );
  },
);

// --- Modal Container ---
/**
 * @deprecated
 */
function ModalActionWidget(props: IPopOverModalProps): JSX.Element {
  const state = useOverlayTriggerState({});
  const actionWidget = props.actionWidget && props.actionWidget(state);

  return (
    <>
      {actionWidget}
      {state.isOpen && (
        <ModalControlledOverlay title={props.title} onCancel={props.onCancel} {...state}>
          {props.children}
        </ModalControlledOverlay>
      )}
    </>
  );
}

/**
 * @deprecated
 */
function ModalControlledOverlay(
  props: Omit<IPopOverModalProps, "actionWidget"> & Pick<OverlayTriggerState, "close" | "isOpen">,
): JSX.Element | null {
  const overlayRef = useRef(null);
  const children = props.children && props.children({ close: props.close });

  return props.isOpen ? (
    // <OverlayContainer style={props.onTop ? { position: "relative", zIndex: 401 } : undefined}>
    <OverlayContainer style={props.style}>
      <ModalOverlay
        ref={overlayRef}
        title={props.title ?? ""}
        isOpen={true}
        onClose={() => {
          typeof props.onCancel === "function" && props.onCancel();
          if (typeof props.close === "function") props.close();
        }}
        isKeyboardDismissDisabled={props.isKeyboardDismissDisabled}
        isDismissable
      >
        {children as any}
      </ModalOverlay>
    </OverlayContainer>
  ) : null;
}

// --- PopOver Container ---
/**
 * @deprecated
 */
function PopOverActionWidget({ children, ...props }: IPopOverModalProps & { positionOffset?: number }): JSX.Element {
  const state = useOverlayTriggerState(props);
  const actionWidget = props.actionWidget && props.actionWidget(state);
  const triggerRef = useRef(null);

  // Get props for the trigger and overlay. This also handles
  // hiding the overlay when a parent element of the trigger scrolls
  // (which invalidates the popover positioning).
  const { triggerProps, overlayProps } = useOverlayTrigger({ type: "dialog" }, state, triggerRef);

  return (
    <>
      <span ref={triggerRef}>{actionWidget && cloneElement(actionWidget, { ...triggerProps })}</span>
      {state.isOpen && (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <PopOverControlledOverlay {...mergeProps(props, state, overlayProps)} targetRef={triggerRef}>
          {children}
        </PopOverControlledOverlay>
      )}
    </>
  );
}

/**
 * @deprecated
 */
function PopOverControlledOverlay({
  targetRef,
  positionOffset,
  close,
  ...props
}: IPositionOverlayProps &
  Omit<IPopOverModalProps, "actionWidget"> &
  Pick<MenuTriggerState, "close" | "isOpen"> & { positionOffset?: number }): JSX.Element | null {
  const overlayRef = useRef(null);
  const children = props.children && (props.children({ close }) as unknown as FC<ModalChildProps>);

  // Get popover positioning props relative to the trigger
  const { overlayProps: positionProps } = useOverlayPosition({
    targetRef,
    overlayRef,
    placement: "bottom",
    offset: positionOffset ?? 13,
    isOpen: props.isOpen,
  });

  return props.isOpen ? (
    // <OverlayContainer style={props.onTop ? { position: "relative", zIndex: 401 } : undefined}>
    <OverlayContainer style={props.style}>
      <ModalOverlay
        {...mergeProps(props, positionProps)}
        ref={overlayRef}
        title={props.title}
        // isOpen={props.isOpen}
        onClose={() => {
          typeof props.onCancel === "function" && props.onCancel();
          close();
        }}
        lightOverlay
        isDismissable
      >
        {props.isOpen && children}
      </ModalOverlay>
    </OverlayContainer>
  ) : null;
}

// --- Menu Container ---
/**
 * Use useMenu instead.
 * @deprecated
 */
function MenuActionWidget(props: Omit<IPopOverModalProps, "title"> & { positionOffset?: number }): JSX.Element {
  const state = useMenuTriggerState(props);
  const actionWidget = props.actionWidget && props.actionWidget(state);
  const triggerRef = useRef(null);

  // Get props for the trigger and overlay. This also handles
  // hiding the overlay when a parent element of the trigger scrolls
  // (which invalidates the popover positioning).
  const { triggerProps, overlayProps } = useOverlayTrigger({ type: "menu" }, state, triggerRef);

  // Close the menu on window resize
  useEffect(() => {
    const close = () => state.close();
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("resize", close);
    };
  }, [state]);

  return (
    <>
      <span ref={triggerRef}>{actionWidget && cloneElement(actionWidget, { ...triggerProps })}</span>
      {state.isOpen && (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <MenuControlledOverlay
          onCancel={props.onCancel}
          {...mergeProps(state, overlayProps)}
          targetRef={triggerRef}
          positionOffset={props.positionOffset}
        >
          {props.children}
        </MenuControlledOverlay>
      )}
    </>
  );
}

/**
 * Use useMenu instead
 * @deprecated
 */
function MenuControlledOverlay({
  targetRef,
  positionOffset,
  close,
  ...props
}: IPositionOverlayProps &
  Omit<IPopOverModalProps, "actionWidget" | "title"> &
  Pick<MenuTriggerState, "close" | "isOpen"> & { positionOffset?: number }): JSX.Element | null {
  const overlayRef = useRef(null);
  const children = props.children && (props.children({ close }) as unknown as FC<ModalChildProps>);

  // Get popover positioning props relative to the trigger
  const { overlayProps: positionProps } = useOverlayPosition({
    targetRef,
    overlayRef,
    placement: "bottom",
    offset: positionOffset ?? 3,
    isOpen: props.isOpen,
  });

  return props.isOpen ? (
    // <OverlayContainer style={props.onTop ? { position: "relative", zIndex: 401 } : undefined}>
    <OverlayContainer style={props.style}>
      <MenuOverlay
        {...mergeProps(props, positionProps)}
        ref={overlayRef}
        // isOpen={props.isOpen}
        onClose={() => {
          typeof props.onCancel === "function" && props.onCancel();
          close();
        }}
        shouldCloseOnBlur
      >
        {props.isOpen && children}
      </MenuOverlay>
    </OverlayContainer>
  ) : null;
}

export {
  MenuActionWidget,
  MenuControlledOverlay,
  ModalActionWidget,
  ModalControlledOverlay,
  PopOverActionWidget,
  PopOverControlledOverlay,
};
